<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { RotateCcw, Loader2 } from 'lucide-vue-next';
import MenuPage from '../views/MenuPage.vue';
import CheckoutView from '../views/CheckoutView.vue';
import CartRail from '../components/cart/CartRail.vue';
import { useCartStore } from '../stores/cart-store';
import { useSessionStore } from '../stores/session-store';

defineProps<{
  currentView: 'menu' | 'details' | 'orders';
  sessionReady: boolean;
}>();

const emit = defineEmits<{
  (event: 'view-change', view: 'menu' | 'details' | 'orders'): void;
  (event: 'order-success', orderId: string | number): void;
  (event: 'open-profile'): void;
}>();

const cartStore = useCartStore();
const sessionStore = useSessionStore();
const secondsLeft = ref(90);
let intervalId: number | null = null;

const resetKiosk = () => {
  cartStore.abandonCart();
  emit('view-change', 'menu');
  secondsLeft.value = 90;
};

const markActivity = () => {
  secondsLeft.value = 90;
};

onMounted(() => {
  window.addEventListener('pointerdown', markActivity);
  window.addEventListener('keydown', markActivity);
  intervalId = window.setInterval(() => {
    secondsLeft.value -= 1;
    if (secondsLeft.value <= 0) {
      resetKiosk();
    }
  }, 1000);
});

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', markActivity);
  window.removeEventListener('keydown', markActivity);
  if (intervalId !== null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
});
</script>

<template>
  <section class="kiosk-client-screen" data-client-screen="kiosk" data-testid="client-kiosk-screen">
    <a href="#main" class="skip-link">Saltar al contenido principal</a>
    <header class="kiosk-client-screen__header">
      <div>
        <span>Autoservicio</span>
        <h1>Toca para ordenar</h1>
      </div>
      <button type="button" class="kiosk-client-screen__reset" @click="resetKiosk">
        <RotateCcw :size="28" />
        Reiniciar {{ secondsLeft }}s
      </button>
    </header>

    <main id="main" tabindex="-1" class="kiosk-client-screen__layout">
      <section class="kiosk-client-screen__menu">
        <div v-if="!sessionReady" class="kiosk-client-screen__loading">
          <Loader2 class="kiosk-client-screen__spinner" :size="32" />
          <p>Iniciando kiosko...</p>
        </div>
        <MenuPage v-if="currentView !== 'details' && sessionReady" />
        <CheckoutView
          v-else-if="currentView === 'details'"
          :session-ready="sessionReady"
          @back="emit('view-change', 'menu')"
          @success="emit('order-success', $event)"
          @open-profile="emit('open-profile')"
        />
      </section>
      <CartRail
        compact
        title="Tu pedido"
        checkout-label="Enviar orden"
        @checkout="emit('view-change', 'details')"
      />
    </main>
  </section>
</template>

<style scoped>
.kiosk-client-screen {
  min-height: 100vh;
  background:
    radial-gradient(1200px 420px at 0% 0%, rgba(15, 118, 110, 0.14), transparent 55%),
    linear-gradient(160deg, #f3f6f4 0%, #ebf0ec 100%);
}

.kiosk-client-screen__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.25rem 1.6rem;
  color: #fff;
  background: linear-gradient(145deg, #111827, #172033);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.24);
}

.kiosk-client-screen__header span {
  font-size: 0.92rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #99f6e4;
}

.kiosk-client-screen__header h1 {
  margin: 0.2rem 0 0;
  font-size: clamp(2rem, 4vw, 4rem);
  line-height: 1.02;
}

.kiosk-client-screen__reset {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 72px;
  border: 2px solid rgba(255, 255, 255, 0.38);
  border-radius: 12px;
  padding: 0 1.2rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  font-size: 1.15rem;
  font-weight: 900;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
}

.kiosk-client-screen__reset:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.kiosk-client-screen__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 430px);
  gap: 1.2rem;
  width: min(1500px, calc(100vw - 2rem));
  margin: 0 auto;
  padding: 1.2rem 0 1.4rem;
}

.kiosk-client-screen__menu {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #d5dfd9;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
}

:deep(button),
:deep(.quick-chip),
:deep(.tab-button),
:deep(.cart-rail__checkout) {
  min-height: 56px;
  font-size: 1.05rem;
}

@media (max-width: 980px) {
  .kiosk-client-screen__layout {
    grid-template-columns: 1fr;
  }
}

.kiosk-client-screen__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: #64748b;
  font-size: 1.2rem;
  font-weight: 600;
}

.kiosk-client-screen__spinner {
  animation: kiosk-spin 1s linear infinite;
}

@keyframes kiosk-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
